import React, { useState, useEffect } from 'react';
import {
  Shield, Lock, Database, Fingerprint, Calendar, Server, Award,
  Users, TrendingUp, Activity, CheckCircle, Clock, Trash2
} from 'lucide-react';
import { useSecurity } from '../security/SecurityContext';
import { API, authFetch } from '../lib/api';
import CollapsibleCard from '../components/CollapsibleCard';
import { TierIntegrityPill } from '../components/TierIntegrityPanel';

// ── Sub-components ──────────────────────────────────────────────────────────
function DNAItem({ label, name, role }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', background: '#f8fafc', borderRadius: 8, border: '1px solid #f1f5f9' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span className="badge badge-blue">{label}</span>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>{name}</div>
          <div style={{ fontSize: 9, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.1em', fontWeight: 700 }}>{role}</div>
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ fontSize: 9, fontWeight: 700, color: '#16a34a', textTransform: 'uppercase' }}>Active</span>
        <span className="status-dot dot-green dot-pulse" />
      </div>
    </div>
  );
}

function GuardRow({ label, ok, note }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #f1f5f9' }}>
      <span style={{ fontSize: 11, fontWeight: 600, color: '#64748b' }}>{label}</span>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        {note && <span style={{ fontSize: 9, color: '#94a3b8' }}>{note}</span>}
        <span className={`badge ${ok ? 'badge-green' : 'badge-amber'}`}>{ok ? 'LOCKED ✓' : 'OFFLINE'}</span>
      </div>
    </div>
  );
}

// ── Tenant stat card (enhanced) ─────────────────────────────────────────────
function TenantStatCard({ icon: Icon, label, value, sub, color, testid }) {
  return (
    <div
      data-testid={testid}
      style={{
        background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12,
        padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 8,
        transition: 'box-shadow 0.15s',
        borderLeft: `3px solid ${color}`,
      }}
      onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.07)'; }}
      onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.1em' }}>{label}</span>
        <div style={{ width: 28, height: 28, borderRadius: 7, background: color + '18', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Icon size={14} color={color} />
        </div>
      </div>
      <div style={{ fontSize: 26, fontWeight: 800, color: '#0f172a', lineHeight: 1 }}>{value ?? '—'}</div>
      {sub && <div style={{ fontSize: 11, color: '#64748b' }}>{sub}</div>}
    </div>
  );
}

// ── Horizontal bar for tier breakdown ───────────────────────────────────────
const TIER_COLORS = {
  LIT: '#3b82f6', ENT: '#8b5cf6', STD: '#10b981',
  PRO: '#f59e0b', EXP: '#ec4899',
};

function TierBar({ code, total, active, revenue, maxTotal }) {
  const pct   = maxTotal > 0 ? Math.max(4, (total / maxTotal) * 100) : 4;
  const color = TIER_COLORS[code] || '#94a3b8';
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: color, display: 'inline-block' }} />
          <span style={{ fontSize: 12, fontWeight: 700, color: '#0f172a' }}>{code}</span>
        </div>
        <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
          <span style={{ fontSize: 11, color: '#64748b' }}>{active}/{total} active</span>
          {revenue > 0 && <span style={{ fontSize: 11, fontWeight: 700, color: '#16a34a' }}>₹{Number(revenue).toLocaleString()}</span>}
        </div>
      </div>
      <div style={{ height: 8, borderRadius: 4, background: '#f1f5f9', overflow: 'hidden' }}>
        <div style={{
          height: '100%', borderRadius: 4,
          background: `linear-gradient(90deg, ${color} 0%, ${color}bb 100%)`,
          width: `${pct}%`,
          transition: 'width 0.6s ease',
          boxShadow: `0 0 6px ${color}44`,
        }} />
      </div>
    </div>
  );
}

// ── Dashboard ────────────────────────────────────────────────────────────────
export default function Dashboard() {
  const { fingerprint, guardStatus, mssqlConnected, chainHeight } = useSecurity();
  const [reminders,  setReminders]  = useState([]);
  const [overview,   setOverview]   = useState(null);

  useEffect(() => {
    authFetch(`${API}/license/reminders`).then(r => r.json()).then(setReminders).catch(() => {});
    authFetch(`${API}/licensor/overview`).then(r => r.json()).then(setOverview).catch(() => {});
  }, []);

  const counts       = overview?.counts        || {};
  const tierData     = overview?.tierBreakdown || [];
  const maxTierTotal = Math.max(1, ...tierData.map(t => t.Total || 0));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24, maxWidth: 1200 }}>

      {/* Tier Integrity pill */}
      <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
        <TierIntegrityPill onNavigate={() => window.dispatchEvent(new CustomEvent('erp-navigate', { detail: { tab: 'licensor', subTab: 'integrity' } }))} />
      </div>

      {/* ── Row 1: Security stats ── */}
      <div className="stat-grid">
        <div className="stat-card" data-testid="stat-chain-height">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
            <div className="stat-label">Chain Height</div>
            <div className="stat-icon-wrap"><Database size={16} color="#3b82f6" /></div>
          </div>
          <div className="stat-value">{chainHeight}</div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <div className="stat-sub">Mined Blocks</div>
            <div className="stat-trend">Active</div>
          </div>
        </div>

        <div className="stat-card" data-testid="stat-security-node">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
            <div className="stat-label">Security Node</div>
            <div className="stat-icon-wrap"><Shield size={16} color="#3b82f6" /></div>
          </div>
          <div className="stat-value" style={{ fontSize: 18 }}>vEritEqcLiQ</div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <div className="stat-sub">Blockchain DNA</div>
            <div className="stat-trend">Online</div>
          </div>
        </div>

        <div className="stat-card" data-testid="stat-threats">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
            <div className="stat-label">Threats</div>
            <div className="stat-icon-wrap"><Lock size={16} color="#10b981" /></div>
          </div>
          <div className="stat-value">0</div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <div className="stat-sub">Neutralized Today</div>
            <div className="stat-trend">Safe</div>
          </div>
        </div>

        <div className="stat-card" data-testid="stat-db-link">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
            <div className="stat-label">DB Link</div>
            <div className="stat-icon-wrap"><Server size={16} color={mssqlConnected ? '#10b981' : '#f59e0b'} /></div>
          </div>
          <div className="stat-value" style={{ fontSize: 18 }}>{mssqlConnected ? 'LIVE' : 'FILE MODE'}</div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <div className="stat-sub">RC26masterDB</div>
            <div className="stat-trend">{mssqlConnected ? 'Sync' : 'Degraded'}</div>
          </div>
        </div>
      </div>

      {/* ── Row 2: Tenant overview cards ── */}
      {mssqlConnected && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 14 }}>
          <TenantStatCard
            icon={CheckCircle} label="Active Tenants"
            value={counts.ActiveClients}
            sub={`of ${counts.TotalClients || 0} total registered`}
            color="#10b981" testid="stat-active-tenants"
          />
          {counts.PendingClients > 0 && (
            <TenantStatCard
              icon={Clock} label="Pending Activation"
              value={counts.PendingClients}
              sub="Provisioning incomplete"
              color="#f59e0b" testid="stat-pending-tenants"
            />
          )}
          <TenantStatCard
            icon={Activity} label="Active Licenses"
            value={counts.ActiveLicenses}
            sub={`of ${counts.TotalLicenses || 0} total issued`}
            color="#6366f1" testid="stat-active-licenses"
          />
          <TenantStatCard
            icon={TrendingUp} label="License Revenue"
            value={counts.TotalLicenseAmount > 0 ? `₹${Number(counts.TotalLicenseAmount).toLocaleString()}` : '—'}
            sub="Total license value"
            color="#3b82f6" testid="stat-revenue"
          />
          <TenantStatCard
            icon={Users} label="System Users"
            value={counts.SystemUsersCount}
            sub={`${counts.SuperAdminCount || 0} super-admins`}
            color="#8b5cf6" testid="stat-system-users"
          />
          {counts.LicensesExpiring30d > 0 && (
            <TenantStatCard
              icon={Calendar} label="Expiring (30d)"
              value={counts.LicensesExpiring30d}
              sub="Licenses near expiry"
              color="#ef4444" testid="stat-expiring"
            />
          )}
        </div>
      )}

      {/* ── Row 3: Tier breakdown + Renewal alerts ── */}
      {mssqlConnected && (
        <div style={{ display: 'grid', gridTemplateColumns: tierData.length > 0 ? '1fr 1fr' : '1fr', gap: 16 }}>

          {/* Tier breakdown chart */}
          {tierData.length > 0 && (
            <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 20 }}
              data-testid="tier-breakdown-chart">
              <div style={{ fontSize: 12, fontWeight: 700, color: '#0f172a', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 6 }}>
                <Database size={13} color="#6366f1" /> Tier Distribution
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {tierData.map(t => (
                  <TierBar
                    key={t.LicenseTier}
                    code={t.LicenseTier}
                    total={t.Total}
                    active={t.Active}
                    revenue={t.Revenue}
                    maxTotal={maxTierTotal}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Client status breakdown */}
          {counts.TotalClients > 0 && (
            <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 20 }}
              data-testid="client-status-chart">
              <div style={{ fontSize: 12, fontWeight: 700, color: '#0f172a', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 6 }}>
                <Users size={13} color="#6366f1" /> Client Status Overview
              </div>
              {[
                { label: 'Active',  value: counts.ActiveClients  || 0, color: '#10b981', icon: CheckCircle },
                { label: 'Pending', value: counts.PendingClients || 0, color: '#f59e0b', icon: Clock },
                { label: 'Other',   value: (counts.TotalClients || 0) - (counts.ActiveClients || 0) - (counts.PendingClients || 0), color: '#94a3b8', icon: Trash2 },
              ].filter(r => r.value > 0).map(row => (
                <div key={row.label} style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
                  <div style={{ width: 28, height: 28, borderRadius: 7, background: row.color + '18', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <row.icon size={13} color={row.color} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                      <span style={{ fontSize: 12, fontWeight: 600, color: '#475569' }}>{row.label}</span>
                      <span style={{ fontSize: 13, fontWeight: 800, color: '#0f172a' }}>{row.value}</span>
                    </div>
                    <div style={{ height: 6, borderRadius: 3, background: '#f1f5f9' }}>
                      <div style={{
                        height: '100%', borderRadius: 3,
                        background: row.color,
                        width: `${Math.max(4, (row.value / Math.max(1, counts.TotalClients)) * 100)}%`,
                        transition: 'width 0.5s ease',
                      }} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Renewal alerts */}
      {reminders.length > 0 && (
        <div className="alert-banner amber animate-fade-in">
          <Calendar size={16} color="#d97706" style={{ flexShrink: 0, marginTop: 2 }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#92400e', marginBottom: 8 }}>Critical Renewal Alerts</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {reminders.map((r) => (
                <div key={r.clientId || r.clientName} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#fff', borderRadius: 6, padding: '6px 12px', border: '1px solid #fde68a' }}>
                  <span style={{ fontSize: 12, fontWeight: 700 }}>{r.clientName}</span>
                  <span style={{ fontSize: 10, color: '#64748b' }}>Expires {new Date(r.expiryDate).toLocaleDateString()}</span>
                  <span className={`badge ${r.daysLeft <= 7 ? 'badge-red' : 'badge-amber'}`}>
                    {r.daysLeft === 1 ? 'Tomorrow' : `${r.daysLeft}d left`}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* DNA + Guard Status */}
      <div className="responsive-stack" style={{ display: 'grid', gridTemplateColumns: '3fr 2fr', gap: 16 }}>
        <CollapsibleCard
          title="Identity DNA Signature Proof"
          badge="SHA-512 / MSN-CLIQ ROOT"
          icon={<Fingerprint size={14} color="var(--brand-blue)" />}
          defaultOpen={false}
          testid="dna-signature-proof"
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <DNAItem label="Primary"   name="Sivannarayana Manchala" role="Root Owner"         />
            <DNAItem label="Co-Owner"  name="Kavitha Manchala"       role="Board of Directors" />
          </div>
          <div style={{ marginTop: 10 }}>
            <CollapsibleCard
              title="Additional Board"
              badge="Members"
              icon={<Award size={12} color="var(--text-muted)" />}
              defaultOpen={false}
              testid="tech-lead-credits"
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <DNAItem label="Board" name="Hitesh Venkatasai"      role="System Architecture"/>
                <DNAItem label="Board" name="Chetan Veeranarayana"   role="Security Protocol"  />
              </div>
            </CollapsibleCard>
          </div>
        </CollapsibleCard>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div className="dna-card">
            <Fingerprint size={28} color="#60a5fa" style={{ marginBottom: 12 }} />
            <div style={{ fontSize: 8, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.15em', marginBottom: 6 }}>Public DNA Key</div>
            <div className="hash-display" style={{ marginBottom: 12 }}>
              {fingerprint || 'MSN-CLIQ_DNA_6472_BLOCKCHAIN_FVP_8210_VERIF'}
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              <span className="badge badge-blue">Root Verified</span>
              {guardStatus?.fileLock  && <span className="badge badge-green">File Lock ✓</span>}
              {guardStatus?.mssqlLock && <span className="badge badge-green">MSSQL Lock ✓</span>}
              {!guardStatus?.mssqlLock && <span className="badge badge-amber">MSSQL Offline</span>}
            </div>
          </div>

          <div className="card" style={{ padding: 20 }}>
            <div style={{ fontSize: 9, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 14 }}>Signature Guard Status</div>
            <GuardRow label="File Lock"  ok={guardStatus?.fileLock}  />
            <GuardRow label="MSSQL Lock" ok={guardStatus?.mssqlLock} note={!guardStatus?.mssqlLock ? 'Unreachable' : null} />
            <div style={{ marginTop: 12, background: '#f8fafc', borderRadius: 8, height: 6, overflow: 'hidden' }}>
              <div style={{ background: '#3b82f6', height: '100%', width: '94%', borderRadius: 8, boxShadow: '0 0 8px rgba(59,130,246,0.4)' }} />
            </div>
          </div>
        </div>
      </div>

      <footer style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0', borderTop: '1px solid #e2e8f0', fontSize: 9, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.12em' }}>
        <div style={{ display: 'flex', gap: 16 }}>
          <span>AES-256-GCM</span><span>Blockchain-v4</span><span>DNA Binding</span><span>Node.js+Express</span>
        </div>
        <span>Proprietary System © 2024 MSN-CLIQ vEritEqcLiQ</span>
      </footer>
    </div>
  );
}
